import { z } from 'zod';
import { uuidSchema } from './validation.js';
/**
 * Order and wait (BLU-1506): a just-in-time blank whose supplier is "Order and
 * wait" is bought BEFORE batching, and an order that needs one waits (ON_HOLD,
 * hold_reason 'awaiting_blank') until it arrives. One building PO per (floor
 * owner, floor, supplier) collects the waits; Send makes it submitted, Arrived
 * makes it received (cost only, no stock).
 *
 * Inputs only here, plus the shapes the SQL functions return (the backend
 * parses the jsonb defensively). The organisation and the user always come from
 * auth, so no schema carries an organizationId. Every procedure is for the
 * FLOOR OWNER only: a 3PL client at a shared floor is refused and never sees a
 * waiting status, a supplier, a PO or a cost.
 */
export const SUPPLY_MODES = ['order_and_wait', 'buy_on_print_day'];
export const supplyModeSchema = z.enum(SUPPLY_MODES);
export const SEND_MODES = ['daily', 'when_pressed'];
export const sendModeSchema = z.enum(SEND_MODES);
/** The hold_reason of an order waiting on a blank. */
export const AWAITING_BLANK_HOLD_REASON = 'awaiting_blank';
// ── Reads (To buy cards, Waiting on blank list, PO detail, badge) ─────────────
export const ORDER_AND_WAIT_DEFAULT_LIMIT = 50;
export const ORDER_AND_WAIT_MAX_LIMIT = 200;
export const ORDER_AND_WAIT_MAX_SEARCH = 100;
export const ORDER_AND_WAIT_SCOPES = ['pos', 'po'];
export const orderAndWaitScopeSchema = z.enum(ORDER_AND_WAIT_SCOPES);
/**
 * pos     the floor's building + sent POs (the To buy cards) and its spares
 * po      one PO's header and every row state of its orders (poId required)
 *
 * The orders waiting at the floor are read through `productionOrders.getWaitingOnBlank`.
 */
export const listOrderAndWaitSchema = z
    .object({
    locationId: uuidSchema,
    scope: orderAndWaitScopeSchema,
    poId: uuidSchema.optional(),
    search: z.string().trim().max(ORDER_AND_WAIT_MAX_SEARCH).optional(),
    limit: z.number().int().min(1).max(ORDER_AND_WAIT_MAX_LIMIT).default(ORDER_AND_WAIT_DEFAULT_LIMIT),
    offset: z.number().int().min(0).default(0),
})
    .refine((input) => input.scope !== 'po' || input.poId !== undefined, {
    message: 'A purchase order is required for this scope',
    path: ['poId'],
});
export const countOrderAndWaitSchema = z.object({ locationId: uuidSchema });
/**
 * The Order Queue's Waiting on blank list for the floor owner. A caller who is
 * not the floor owner, or anyone at a floor with no Order and wait and nothing
 * waiting, gets `isOwner: false` and an empty list (never a refusal: the queue
 * page asks on every load).
 */
export const getWaitingOnBlankSchema = z.object({
    locationId: uuidSchema,
    search: z.string().trim().max(ORDER_AND_WAIT_MAX_SEARCH).optional(),
    limit: z.number().int().min(1).max(ORDER_AND_WAIT_MAX_LIMIT).default(ORDER_AND_WAIT_DEFAULT_LIMIT),
    offset: z.number().int().min(0).default(0),
});
// ── Send / Arrived ────────────────────────────────────────────────────────────
export const ORDER_AND_WAIT_MAX_LINES = 500;
/**
 * Send (building -> sent). `outSkuIds` are the blanks marked "Out at {supplier}":
 * their rows move to the next building PO flagged out.
 */
export const sendOrderAndWaitSchema = z.object({
    poId: uuidSchema,
    outSkuIds: z.array(uuidSchema).max(ORDER_AND_WAIT_MAX_LINES).default([]),
});
/**
 * Arrived (sent -> received). `short` lists ONLY the lines that came in short,
 * `{skuId, arrived}`; every other line arrived in full. Arrived 0 for a line
 * rolls it onto the next building PO.
 */
export const arriveOrderAndWaitSchema = z.object({
    poId: uuidSchema,
    short: z
        .array(z.object({
        skuId: uuidSchema,
        arrived: z.number().int().min(0).max(999_999),
    }))
        .max(ORDER_AND_WAIT_MAX_LINES)
        .default([]),
});
// ── Supplier "When to buy" ────────────────────────────────────────────────────
const HH_MM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
/**
 * The supplier's "When to buy" card. Every key is optional (the card may save
 * one row); a key that is present is written, and `null` clears sendTime or
 * leadTimeDays. A daily send needs a time (the stored one or this request's;
 * the database refuses it otherwise as INVALID_INPUT, field sendTime). Admin only.
 */
export const setSupplierSupplySchema = z
    .object({
    supplierId: uuidSchema,
    supplyMode: supplyModeSchema.optional(),
    sendMode: sendModeSchema.optional(),
    sendTime: z.string().regex(HH_MM, 'Use a 24 hour time like 14:00').nullable().optional(),
    leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
})
    .refine((input) => input.supplyMode !== undefined ||
    input.sendMode !== undefined ||
    input.sendTime !== undefined ||
    input.leadTimeDays !== undefined, { message: 'Nothing to change', path: ['supplyMode'] });
